import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'
import { tokenManager, apiClient, authAPI, LoginCredentials, RedeemInvitePayload } from '../lib/api'

interface Office {
  id: number
  name: string
  abbreviation: string
  office_type: string
  priorities: string
  preferred_contract_structures: string
  support_email: string
}

export interface RoleAssignment {
  role: string
  office: { id: number; name: string } | null
}

export interface User {
  id: number
  username: string
  email: string
  email_verified_at: string | null
  first_name: string
  last_name: string
  full_name: string
  // Legacy single-role field. Retained during the RBAC transition (EMC
  // Expand/Migrate); prefer `permissions` for new gating. Removed in a later
  // Contract-phase change.
  role: string
  role_display: string
  phone: string
  organization: string
  office: Office | null
  theme_preference?: 'light' | 'dark' | 'system'
  vendor_id: number | null
  is_superuser?: boolean
  // RBAC capability fields (additive, from /api/users/me/). Optional so the
  // type is tolerant if the backend predates chunk 5.
  role_assignments?: RoleAssignment[]
  permissions?: string[]
  offices?: number[]
  sees_all_offices?: boolean
}

interface UserContextType {
  user: User | null
  loading: boolean
  error: string | null
  refetch: () => Promise<User | null>
  /** Sign in and load the user. Resolves to null if the user fails to load. */
  login: (credentials: LoginCredentials) => Promise<User | null>
  /** Create an account from an invite, sign in and load the user. */
  redeemInvite: (payload: RedeemInvitePayload) => Promise<User | null>
  logout: () => void
  updateThemePreference: (theme: 'light' | 'dark' | 'system') => Promise<void>
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Bumped on every fetch and on sign-out, so a response for a previous
  // session can't land after the user has changed.
  const requestSeq = useRef(0)

  const applyTheme = (theme: 'light' | 'dark' | 'system') => {
    let shouldBeDark = false
    
    if (theme === 'dark') {
      shouldBeDark = true
    } else if (theme === 'light') {
      shouldBeDark = false
    } else if (theme === 'system') {
      shouldBeDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    
    if (shouldBeDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    
    // Also update localStorage for immediate persistence
    localStorage.setItem('theme', shouldBeDark ? 'dark' : 'light')
  }

  const fetchUser = async (): Promise<User | null> => {
    const seq = ++requestSeq.current
    if (!tokenManager.isAuthenticated()) {
      setUser(null)
      setLoading(false)
      // Apply theme from localStorage if not logged in
      const storedTheme = localStorage.getItem('theme')
      if (storedTheme) {
        applyTheme(storedTheme as 'light' | 'dark')
      }
      return null
    }

    try {
      setLoading(true)
      setError(null)
      // Use apiClient so expired tokens are refreshed and the request is retried (avoids "user data not retrieved" on refresh)
      const userData = await apiClient.get<User>('/api/users/me/')
      if (seq !== requestSeq.current) return null
      setUser(userData)
      // Apply theme from user preference
      if (userData.theme_preference) {
        applyTheme(userData.theme_preference)
      }
      return userData
    } catch (err) {
      if (seq !== requestSeq.current) return null
      console.error('Error fetching user:', err)
      setError(err instanceof Error ? err.message : 'Failed to load user')
      return null
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }

  const updateThemePreference = async (theme: 'light' | 'dark' | 'system') => {
    // Apply theme immediately for instant feedback
    applyTheme(theme)

    if (!user) {
      return
    }

    try {
      const updatedUser = await apiClient.patch<User>('/api/users/me/', { theme_preference: theme })
      setUser(updatedUser)
    } catch (err) {
      console.error('Error updating theme preference:', err)
      throw err
    }
  }

  // Sign-in and sign-out go through the provider so the user always matches
  // the session. Clearing the old user first means nothing renders a previous
  // session's user while the new one loads.
  const startSession = () => {
    setUser(null)
    return fetchUser()
  }

  const login = async (credentials: LoginCredentials) => {
    await authAPI.login(credentials)
    return startSession()
  }

  const redeemInvite = async (payload: RedeemInvitePayload) => {
    await authAPI.redeemInvite(payload)
    return startSession()
  }

  const logout = () => {
    authAPI.logout()
    requestSeq.current++
    setUser(null)
    setError(null)
    setLoading(false)
  }

  useEffect(() => {
    fetchUser()
  }, [])

  return (
    <UserContext.Provider
      value={{ user, loading, error, refetch: fetchUser, login, redeemInvite, logout, updateThemePreference }}
    >
      {children}
    </UserContext.Provider>
  )
}

export const useUser = (): UserContextType => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

/**
 * The loaded user, for components rendered under ProtectedRoute — which
 * doesn't render its children until the user has loaded, so this is never
 * null there.
 */
export const useSignedInUser = (): User => {
  const { user } = useUser()
  if (!user) {
    throw new Error('useSignedInUser must be used under ProtectedRoute')
  }
  return user
}
